import { useContext, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Apple,
  Play,
  Download as DownloadIcon,
  Copy,
  Share2,
  ScanLine,
  Calculator,
  Smartphone,
  QrCode,
} from "lucide-react";
import { SettingsContext } from "../App";
import Reveal from "../components/Reveal";
import TutorialModal from "../components/TutorialModal";
import { useLang } from "../i18n/i18n";
import { track } from "../lib/track";

export default function DownloadPage() {
  const { t } = useLang();
  const settings = useContext(SettingsContext) || {};
  const url = `${window.location.origin}/telecharger`;
  const [sharing, setSharing] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  const links = [
    { id: "play", icon: Play, label: "Google Play", url: settings.play_store_url },
    { id: "appstore", icon: Apple, label: "App Store", url: settings.app_store_url || "https://apps.apple.com/us/app/instagoldcalc/id6824466775" },
    { id: "apk", icon: DownloadIcon, label: "APK", url: settings.apk_url },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("dlpage.copied"));
    } catch {
      toast.error(t("dlpage.copyFail") + url);
    }
  };

  const share = async () => {
    setSharing(true);
    if (navigator.share) {
      try {
        await navigator.share({ title: "InstaGold Calc", text: t("dlpage.qrCaption"), url });
      } catch {}
    } else {
      await copyLink();
    }
    setSharing(false);
  };

  const steps = [
    { icon: ScanLine, tt: "dlpage.step1t", dd: "dlpage.step1d" },
    { icon: Smartphone, tt: "dlpage.step2t", dd: "dlpage.step2d" },
    { icon: Calculator, tt: "dlpage.step3t", dd: "dlpage.step3d" },
  ];

  const btnBase = "inline-flex items-center gap-3 rounded-xl border px-6 py-4 transition-all";

  return (
    <main className="pt-[72px]" data-testid="download-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-[1.2fr_1fr] items-center">
          <Reveal>
            <p className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase text-gold">
              <QrCode size={15} /> {t("dlpage.eyebrow")}
            </p>
            <h1 className="mt-4 font-display text-4xl sm:text-6xl font-light text-cream leading-tight">
              InstaGold Calc,
              <br />
              <em className="gold-text italic font-medium">{t("dlpage.title2")}</em>
            </h1>
            <p className="mt-6 max-w-lg text-base sm:text-lg leading-relaxed text-cream/70">{t("dlpage.sub")}</p>

            <div className="mt-9 flex flex-wrap gap-4">
              {links.map((l) => (
                <a
                  key={l.id}
                  href={l.url || "#"}
                  target={l.url ? "_blank" : undefined}
                  rel="noreferrer"
                  onClick={(e) => {
                    if (!l.url) e.preventDefault();
                    else track("download", l.id);
                  }}
                  data-testid={`page-download-${l.id}-btn`}
                  className={`${btnBase} ${
                    l.url
                      ? "border-gold/40 bg-gold/10 text-goldlight hover:bg-gold hover:text-ink hover:shadow-[0_0_30px_rgba(212,175,55,0.35)]"
                      : "border-cream/12 text-mutedc cursor-not-allowed"
                  }`}
                >
                  <l.icon size={20} />
                  <span className="text-left leading-tight">
                    <span className="block text-[10px] uppercase tracking-[0.2em] opacity-70">
                      {l.url ? t("dlpage.downloadOn") : t("dlpage.soonOn")}
                    </span>
                    <span className="block text-sm font-semibold">{l.label}</span>
                  </span>
                </a>
              ))}
            </div>

            {settings.download_note && (
              <p className="mt-5 text-sm text-mutedc" data-testid="page-download-note">
                {settings.download_note}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={share}
                disabled={sharing}
                data-testid="page-share-btn"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-ink transition-all hover:bg-goldlight disabled:opacity-60"
              >
                <Share2 size={15} /> {t("dlpage.share")}
              </button>
              <button
                onClick={copyLink}
                data-testid="page-copy-link-btn"
                className="inline-flex items-center gap-2 rounded-full border border-cream/20 px-6 py-3 text-sm text-cream/80 transition-all hover:border-gold hover:text-goldlight"
              >
                <Copy size={15} /> {t("dlpage.copy")}
              </button>
            </div>

            <div className="mt-6">
              <button
                onClick={() => setShowTutorial(true)}
                data-testid="page-tutorial-open"
                className="group inline-flex items-center gap-3 text-base font-semibold text-cream transition-colors hover:text-goldlight"
              >
                <span className="relative grid h-12 w-12 place-items-center rounded-full bg-gold shadow-[0_0_28px_rgba(212,175,55,0.5)] transition-transform duration-300 group-hover:scale-105">
                  <span className="absolute inset-0 rounded-full bg-gold/50 animate-ping opacity-40" />
                  <Play size={18} fill="currentColor" className="relative ml-0.5 text-ink" />
                </span>
                {t("hero.tutorial")}
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="flex justify-center">
            <div className="text-center">
              <div className="inline-block rounded-3xl bg-gradient-to-br from-goldlight via-[#E8C766] to-golddeep p-5 sm:p-7 shadow-[0_0_70px_rgba(212,175,55,0.28)]" data-testid="qr-card">
                <div className="rounded-2xl bg-cream p-4">
                  <QRCodeSVG value={url} size={200} bgColor="transparent" fgColor="#0B0A07" level="M" data-testid="qr-code" />
                </div>
              </div>
              <p className="mt-5 text-sm text-cream/70" data-testid="qr-caption">
                {t("dlpage.qrCaption")}
              </p>
              <p className="mt-1 text-xs text-mutedc tabular" data-testid="qr-url">
                {url.replace(/^https?:\/\//, "")}
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-20 grid gap-5 md:grid-cols-3">
          {steps.map((s, i) => (
            <Reveal key={s.tt} delay={0.08 * i} className="h-full">
              <div className="h-full rounded-2xl border border-gold/15 bg-panel/70 p-8 transition-all hover:border-gold/40 hover:-translate-y-1 duration-300">
                <span className="font-display text-3xl text-gold/40 tabular">0{i + 1}</span>
                <s.icon size={24} className="mt-4 text-gold" strokeWidth={1.5} />
                <h3 className="mt-3 font-display text-xl text-cream">{t(s.tt)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-cream/60">{t(s.dd)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {showTutorial && <TutorialModal onClose={() => setShowTutorial(false)} />}
      </AnimatePresence>
    </main>
  );
}
